import { useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowRight, Plus } from "lucide-react"
import { CHANNEL_STATUS_LABELS, PROVIDER_LABELS } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ChannelIcon } from "@/components/workspace/channel-icon"
import { Heading, Hint } from "@/components/workspace/primitives"
import { formatDateTime } from "@/lib/format"
import { channelsQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/settings/channels")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(channelsQuery(params.workspace)),
  component: Channels,
})

/** Screen 42. */
function Channels() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const { connections, sandbox } = useSuspenseQuery(channelsQuery(workspace.slug)).data
  const params = { workspace: workspace.slug }
  const manage = can("manage_workspace")
  return (
    <>
      <Heading
        title="Channels"
        description="Your customer conversations, connected."
        action={
          manage ? (
            <Button asChild>
              <Link to="/w/$workspace/channels/new" params={params}>
                <Plus />
                Connect channel
              </Link>
            </Button>
          ) : undefined
        }
      />
      {sandbox && <p className="ws-hint mb-4">Sandbox mode: connections are simulated. No Meta account is contacted.</p>}
      <div className="ws-channels-list">
        {connections.map((channel) => {
          const detail =
            channel.status === "connected"
              ? channel.lastSyncedAt
                ? `Receiving messages · last synced ${formatDateTime(channel.lastSyncedAt)}`
                : "Connected · waiting for the first message"
              : (channel.issueSummary ?? (channel.status === "disconnected" ? "No new events. Reconnect to resume activity." : "Access changed. Review the connection."))
          return (
            <div className="ws-channel-row" key={channel.id}>
              <span className="ws-feature-icon">
                <ChannelIcon provider={channel.provider} />
              </span>
              <div className="ws-channel-info">
                <div>
                  <h2>{PROVIDER_LABELS[channel.provider]}</h2>
                  <Badge variant={channel.status === "connected" ? "secondary" : "outline"} className={channel.status === "needs_attention" ? "ws-status-warning" : ""}>
                    {CHANNEL_STATUS_LABELS[channel.status]}
                  </Badge>
                </div>
                <strong>{channel.handle ?? channel.displayName}</strong>
                <p>{detail}</p>
              </div>
              {channel.status === "connected" ? (
                <Button variant="outline" asChild>
                  <Link to="/w/$workspace/inbox" params={params}>
                    Open inbox
                    <ArrowRight />
                  </Link>
                </Button>
              ) : channel.status === "needs_attention" ? (
                <Button asChild>
                  <Link to="/w/$workspace/channels/$connectionId" params={{ ...params, connectionId: channel.id }}>
                    Review access
                    <ArrowRight />
                  </Link>
                </Button>
              ) : (
                <Button variant="outline" asChild>
                  <Link to="/w/$workspace/channels/new" params={params} search={{ provider: channel.provider }}>
                    Reconnect
                    <ArrowRight />
                  </Link>
                </Button>
              )}
            </div>
          )
        })}
        {connections.length === 0 && (
          <p className="ws-empty">
            No channels connected yet. {manage ? "Connect Instagram or Messenger to start receiving conversations." : "An admin can connect Instagram or Messenger."}
          </p>
        )}
      </div>
      {connections.length > 0 && (
        <p className="ws-hint mt-3">
          Open a connection for its capabilities and details:{" "}
          {connections.map((c, i) => (
            <span key={c.id}>
              {i > 0 && ", "}
              <Link className="ws-table-link" to="/w/$workspace/channels/$connectionId" params={{ ...params, connectionId: c.id }}>
                {c.handle ?? c.displayName}
              </Link>
            </span>
          ))}
          .
        </p>
      )}
      <Hint title="Know exactly what needs attention">A connection issue affects only the capabilities shown on its detail page. Review its impact before resuming paused work.</Hint>
    </>
  )
}
